import { ApiResponse, JSONSchema } from 'lib-shared-types-api';

import {
  AutomationHandoffTool,
  AutomationTaskTool,
  CustomInstruction,
  ProcedureTool,
  RetrievalTool,
  ToolBase,
} from 'apis/chatbots';

/**
 * Represents how the agent will interact with a configured parameter.
 *
 * - Agentic: The LLM will decide the value
 * - Fixed-value: The user provides a fixed value
 * - Single-choice: LLM will choose from a provided set
 * - Object: A container whose `fields` hold nested configured parameters. This is
 *   only active when dealing with dynamic schema.
 */
/** A single choice entry returned by the API. */
export type ConfiguredChoice = {
  readonly value: string;
  readonly name: string | null;
  readonly values?: readonly { readonly value: string }[];
};

export type ConfiguredParameterValue =
  | { readonly _type: 'agentic' }
  | {
      readonly _type: 'fixed-value';
      readonly value: {
        readonly value: unknown;
        readonly values?: readonly unknown[];
        readonly name?: string | null;
      };
    }
  | {
      readonly _type: 'single-choice';
      readonly choices: readonly ConfiguredChoice[];
    }
  | {
      readonly _type: 'object';
      readonly fields: ConfiguredParameters;
    };

/** Configured parameters for an integration tool. */
export type ConfiguredParameters = Record<string, ConfiguredParameterValue>;

/** Represents an integration tool that connects to external services. */
export type IntegrationTool = ToolBase<'integration-tool'> & {
  /** The integration type identifier (e.g., 'dixa'). */
  readonly integrationId: string;
  /** The action ID within the integration. */
  readonly actionId: string;
  /** The configured parameters for this integration tool. */
  readonly configuredParameters: ConfiguredParameters;
};

/** Union of all tool types. */
export type Tool =
  | AutomationTaskTool
  | AutomationHandoffTool
  | RetrievalTool
  | ProcedureTool
  | IntegrationTool;

export type AgentUser = {
  readonly name?: string;
  readonly avatarUrl?: string | null;
};

export type KnowledgeBaseSettings = {
  readonly id: string;
  readonly includeInternalArticles: boolean;
};

export type KnowledgeBaseReference = {
  readonly id: string;
  readonly name: string;
  readonly includeInternalArticles: boolean;
  readonly state: 'ready' | 'syncing' | 'inactive' | 'failed';
};

export type AgentTestMessenger = {
  readonly id: string;
  readonly token: string;
};

export type AiAgent = {
  readonly id: string;
  readonly organizationId: string;
  readonly chatbotVersionId?: string;
  readonly name?: string;
  readonly knowledgeBases: KnowledgeBaseReference[];
  readonly systemUserId?: string;
  readonly displayName?: string;
  readonly tools: Tool[];
  readonly testMessenger?: AgentTestMessenger;
  readonly personaInstructions?: CustomInstruction[];
  readonly customInstructions?: CustomInstruction[];
  readonly variant?: AiAgentVariant | null;
  readonly keyterms?: AiAgentKeyterm[];
  readonly chatWelcomeMessage?: string | null;
};

export type AiAgentKeyterm = {
  readonly value: string;
};

export type AiAgentListItem = {
  readonly id: string;
  readonly organizationId: string;
  readonly name?: string;
  readonly knowledgeBases: KnowledgeBaseReference[];
  readonly systemUserId?: string;
  readonly displayName?: string;
  readonly testMessenger?: AgentTestMessenger;
  readonly variant?: AiAgentVariant | null;
};

export type AiAgentVariant = 'support' | 'automationbot';

export type CreateAiAgentsApiArg = {
  readonly name: string;
  readonly organizationId: string;
  readonly knowledgeBases: KnowledgeBaseSettings[];
  readonly user?: AgentUser;
  readonly variant?: AiAgentVariant;
};

export type CreateAiAgentsApiResponse = ApiResponse<AiAgent>;

export type ReadAiAgentsApiArg = {
  readonly id: string;
  readonly chatbotVersionId?: string;
};

export type ReadAiAgentsApiResponse = ApiResponse<AiAgent>;

export type AiAgentKnowledgeBaseSource =
  | 'elevio'
  | 'dixa_knowledge'
  | 'website'
  | 'file'
  | 'conversations';

export type AiAgentKnowledgeBase = {
  readonly id: string;
  readonly name: string;
  readonly source: AiAgentKnowledgeBaseSource;
  readonly synced_at: string;
  readonly state: 'ready' | 'syncing' | 'inactive' | 'failed';
  readonly external_source_id: string;
};

export type ReadAiAgentsKnowledgeBasesApiArg = {
  readonly id: string;
};

export type ReadAiAgentsKnowledgeBasesApiResponse = ApiResponse<{
  readonly knowledgeBases: AiAgentKnowledgeBase[];
}>;

export type ReadAiAgentsListApiArg = Record<string, never>;

export type ReadAiAgentsListApiResponse = ApiResponse<{
  readonly agents: AiAgentListItem[];
}>;

export type UpdateAiAgentsApiArg = {
  readonly id: string;
  readonly name?: string;
  readonly user?: AgentUser | null;
  readonly knowledgeBases?: KnowledgeBaseSettings[];
  readonly personaInstructions?: CustomInstruction[] | null;
  readonly customInstructions?: CustomInstruction[] | null;
  readonly keyterms?: AiAgentKeyterm[];
  readonly chatWelcomeMessage?: string;
};

export type UpdateAiAgentsApiResponse = ApiResponse<AiAgent>;

export type DeleteAiAgentsApiArg = {
  readonly id: string;
};

export type CreateAiAgentsDuplicateApiArg = {
  readonly id: string;
};

export type AiAgentConfigCommit = {
  readonly chatbotVersionId: string;
  readonly name: string;
  readonly displayVersion: number;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly deployed: boolean;
};

export type ReadAiAgentsConfigCommitsApiArg = {
  readonly id: string;
};

export type ReadAiAgentsConfigCommitsApiResponse = ApiResponse<{
  readonly configCommits: AiAgentConfigCommit[];
}>;

export type CreateAiAgentsConfigCommitsApiResponse = ApiResponse<{
  readonly chatbotVersionId: string;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly deployed: boolean;
  readonly displayVersion: number;
  readonly name: string;
}>;

export type CreateAiAgentsConfigCommitsApiArg = {
  readonly id: string;
  readonly chatbotVersionId: string;
  readonly name: string;
};

export type CreateAiAgentsDeploymentsApiArg = {
  readonly id: string;
  readonly chatbotVersionId: string;
};

export type CreateAiAgentsTestVersionApiArg = {
  readonly id: string;
  readonly chatbotVersionId: string;
};

export type CreateAiAgentsTestVersionResponse = ApiResponse<{
  readonly message: string;
}>;

export type CreateAiAgentsVersionRestoreApiArg = {
  readonly id: string;
  readonly versionId: string;
  readonly chatbotVersionId: string;
};

export type CreateAiAgentsVersionRestoreApiResponse = ApiResponse<AiAgent>;

// Base types for AI Agent tools
export type CreateAiAgentToolBase<TTool extends Tool> = Omit<
  TTool,
  'createdAt' | 'updatedAt' | 'id' | 'referenceId'
> & {
  readonly id: string; // AI Agent ID
};

export type UpdateAiAgentToolBase<TTool extends Tool> = Omit<
  TTool,
  'createdAt' | 'updatedAt' | 'referenceId'
> & {
  readonly id: string; // AI Agent ID
  readonly toolId: string; // Tool ID (maps to tool.id in body)
};

// Create tool types
export type CreateAiAgentAutomationTaskTool =
  CreateAiAgentToolBase<AutomationTaskTool>;

export type CreateAiAgentAutomationHandoffTool =
  CreateAiAgentToolBase<AutomationHandoffTool>;

export type CreateAiAgentRetrievalTool = CreateAiAgentToolBase<RetrievalTool>;
export type CreateAiAgentProcedureTool = CreateAiAgentToolBase<ProcedureTool>;

export type CreateAiAgentIntegrationTool = Omit<
  CreateAiAgentToolBase<IntegrationTool>,
  'parameters' | 'name' | 'description'
> & {
  readonly name?: string;
  readonly description?: string;
};

export type CreateAiAgentsToolApiArg =
  | CreateAiAgentAutomationTaskTool
  | CreateAiAgentAutomationHandoffTool
  | CreateAiAgentRetrievalTool
  | CreateAiAgentProcedureTool
  | CreateAiAgentIntegrationTool;

export type CreateAiAgentsToolApiResponse = ApiResponse<Tool>;

// Update tool types
export type UpdateAiAgentAutomationTaskTool =
  UpdateAiAgentToolBase<AutomationTaskTool>;

export type UpdateAiAgentAutomationHandoffTool =
  UpdateAiAgentToolBase<AutomationHandoffTool>;

export type UpdateAiAgentRetrievalTool = UpdateAiAgentToolBase<RetrievalTool>;
export type UpdateAiAgentProcedureTool = UpdateAiAgentToolBase<ProcedureTool>;

export type UpdateAiAgentIntegrationTool = Omit<
  UpdateAiAgentToolBase<IntegrationTool>,
  'parameters' | 'name' | 'description'
> & {
  readonly name?: string;
  readonly description?: string;
};

export type CreateAiAgentsToolsByIdApiArg =
  | UpdateAiAgentAutomationTaskTool
  | UpdateAiAgentAutomationHandoffTool
  | UpdateAiAgentRetrievalTool
  | UpdateAiAgentProcedureTool
  | UpdateAiAgentIntegrationTool;

export type CreateAiAgentsToolsByIdApiResponse = ApiResponse<Tool>;

export type ToolTypeFilter =
  | 'automation-task'
  | 'automation-handoff'
  | 'retrieval'
  | 'procedure'
  | 'integration';

export const ALL_TOOL_TYPES: ToolTypeFilter[] = [
  'automation-task',
  'automation-handoff',
  'retrieval',
  'procedure',
] as const;

export type ReadAiAgentsToolsApiArg = {
  readonly id: string;
  readonly offset: number;
  readonly limit: number;
  readonly type: ToolTypeFilter[];
  readonly chatbotVersionId?: string;
};

export type ReadAiAgentsToolsApiResponse = ApiResponse<{
  readonly tools: Tool[];
  readonly nextOffset: number;
}>;

export type ReadAiAgentsToolApiArg = {
  readonly id: string;
  readonly toolId: string;
  readonly chatbotVersionId?: string;
};

export type ReadAiAgentsToolApiResponse = ApiResponse<Tool>;

export type ReadAiAgentsToolReferencesApiArg = {
  readonly id: string;
  readonly toolId: string;
};

export type ReadAiAgentsToolReferencesApiResponse = ApiResponse<{
  readonly procedures: {
    id: string;
    name: string;
  }[];
  readonly customInstructions: {
    title: string;
  }[];
}>;

export type DeleteAiAgentsToolApiArg = {
  readonly id: string;
  readonly toolId: string;
};

export type DeleteAiAgentsToolApiResponse = ApiResponse<Tool>;

export type CreateAiAgentsToolDuplicateApiArg = {
  readonly id: string;
  readonly toolId: string;
};

export type ReadToolsIntegrationsActionsApiArg = {
  readonly integrationType: string;
};

export type ReadToolsIntegrationsActionsApiResponse = readonly JSONSchema[];

type BaseIntegrationActionRunItem = {
  readonly name: string;
  // TODO: add infer tool types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly data: any;
};

export type IntegrationActionRunScalarItem = BaseIntegrationActionRunItem & {
  readonly value: string;
};

export type IntegrationActionRunArrayItem = BaseIntegrationActionRunItem & {
  readonly value: readonly string[];
};

export type IntegrationActionRun =
  | IntegrationActionRunScalarItem
  | IntegrationActionRunArrayItem;

export function isIntegrationActionRunScalarItem(
  item: IntegrationActionRun,
): item is IntegrationActionRunScalarItem {
  return typeof item.value === 'string';
}

export function isIntegrationActionRunArrayItem(
  item: IntegrationActionRun,
): item is IntegrationActionRunArrayItem {
  return Array.isArray(item.value);
}

export type CreateToolsIntegrationsActionsRunApiArg = {
  readonly integrationType: string;
  readonly actionId: string;
  readonly csid?: number;
  readonly configuredParameters?: Record<string, unknown>;
};

export type CreateToolsIntegrationsActionsRunApiResponse = {
  readonly items: readonly IntegrationActionRun[];
  readonly metadata: {
    readonly totalCount: number;
    readonly nextPageToken?: string;
  };
};

export type DefaultProcedure = {
  readonly name: string;
  readonly description: string;
  readonly prompt: string;
};

export type ReadAiAgentsDefaultsApiResponse = ApiResponse<{
  readonly instructions: {
    readonly custom: {
      readonly title: string;
      readonly text: string;
    }[];
    readonly persona: [
      {
        readonly title: string;
        readonly text: string;
      },
    ];
  };
  readonly procedures: DefaultProcedure[];
}>;

export type CreateToolsDynamicSchemaRunApiArg = {
  readonly resolverId: string;
  readonly value: string;
  readonly name?: string;
};

export type CreateToolsDynamicSchemaRunApiResponse = JSONSchema;

export type ChatbotQuotaPeriod = {
  readonly year: number;
  readonly month: number;
};

export type ChatbotQuota = {
  readonly feature: string;
  readonly featureName: string;
  readonly extraLimit: number;
  readonly includedLimit: number;
  readonly maxLimit: number;
  readonly usage: {
    readonly used: number;
    readonly period: ChatbotQuotaPeriod;
  };
  readonly unlimited: boolean;
  readonly limit: number;
};

export type UpdateChatbotsQuotasApiArg = {
  readonly feature: string;
  readonly extraLimit: number;
};

export type UpdateChatbotsQuotasApiResponse = {
  readonly data: readonly ChatbotQuota[];
  readonly meta: Record<string, unknown>;
};

export type ReadChatbotsQuotasV2ApiArg = {
  readonly year: number;
  readonly month: number;
  readonly feature: readonly string[];
};

export type ReadChatbotsQuotasV2ApiResponse = {
  readonly data: {
    readonly quotas: readonly ChatbotQuota[];
    readonly earliestUsagePeriod: ChatbotQuotaPeriod | null;
  };
  readonly meta: Record<string, unknown>;
};
